import { authenticatedFetch } from '../utils/apiClient';
import { SYSTEM_INSTRUCTION, MAX_PROMPT_LENGTH, YOGA_SPECIALIST_INSTRUCTION, NUTRI_HEAL_SPECIALIST_PROMPT } from "../utils/constants";
import { searchVectorDatabase, logAnalyticsEvent, checkRateLimit, supabase } from "./backendService";
import { RemedyDocument, Message, YogaPose, Meal, User, UserProfile } from "../types";
import { validateMealPlan } from "./mealValidator";
import { getAilmentDietaryRules } from "./ailmentDietaryRules";
import {
  getYogaProtocol,
  YogaProtocolTemplate,
  extractYogaSignalsFromConsultation,
  type YogaSignals,
} from "./yogaPoseLibrary";
import {
  getRecipesByAilment,
  getHerbalTeasForAilment,
  STATIC_BASE_GUIDELINES,
  RecipeCategory,
  NUTRIHEAL_RECIPES
} from "./nutriHealLibrary";
import logger from "@/utils/logger";

function getCurrentTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
}

interface DietarySignals {
  mentionedFoods: string[];
  mentionedIngredients: string[];
  avoidanceSignals: string[];
  constitutionType: string | null;
  tonics: string[];
  preparationGuidance: string[];
  primaryFoodCategory: string | null;
  specificDishMentions: string[];
}

export const generateEmbedding = async (text: string): Promise<number[] | null> => {
  try {
    const res = await fetch('/api/gemini', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "embed", prompt: text })
    });
    if (!res.ok) {
      const errBody = await res.text().catch(() => "(unreadable)");
      logger.error(`[GeminiService] Embed HTTP ${res.status}:`, errBody);
      throw new Error(`Embed failed (${res.status}): ${errBody}`);
    }
    const data = await res.json();
    return data.embeddings?.[0]?.values || null;
  } catch (e) {
    logger.error("[GeminiService] Embedding error:", e);
    return null;
  }
};

const fetchOnboardingProfile = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('onboarding_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.warn("[GeminiService] Failed to fetch onboarding profile:", error);
    return null;
  }
};

export const sendMessageWithRAG = async function* (
  message: string,
  history: Message[],
  userTier: 'Free' | 'Premium',
  queryCount: number,
  currentUser: User | null,
  onSourcesFound?: (sources: RemedyDocument[]) => void,
  isRefinement: boolean = false
) {
  try {
    // 1. Check Rate Limit first (SKIP if refinement)
    if (!isRefinement) {
      await checkRateLimit();
    }

    // 2. Enforce Daily Query Limit (if Applicable) - SKIP if refinement
    const { DAILY_QUERY_LIMIT } = await import("../utils/constants");
    if (!isRefinement && userTier === 'Free' && queryCount >= DAILY_QUERY_LIMIT) {
      yield "You have reached your daily free limit. Please upgrade for unlimited access.";
      return;
    }
    const safeMessage = message.substring(0, MAX_PROMPT_LENGTH);
    let contextDocs: RemedyDocument[] = [];
    let hasRAG = false;

    // 1. Vector Search
    const queryVector = await generateEmbedding(safeMessage);
    if (queryVector) {
      contextDocs = await searchVectorDatabase(safeMessage, queryVector);
      if (contextDocs.length > 0) {
        if (onSourcesFound) onSourcesFound(contextDocs);
        hasRAG = true;
      }
    }

    // 1.5 Fetch User Profile & Onboarding Data
    let userContext = "";
    if (currentUser) {
      try {
        logger.log("[GeminiService] Fetching profile for context injection...");
        const { fetchUserProfile } = await import("./backendService");

        const [profile, onboarding] = await Promise.all([
          fetchUserProfile(currentUser.id),
          fetchOnboardingProfile(currentUser.id)
        ]);

        if (profile || onboarding) {
          const prakriti = profile?.prakriti_dosha || onboarding?.prakriti_dosha || onboarding?.vikriti_dosha;
          const vikriti = profile?.vikriti_dosha || onboarding?.vikriti_dosha;
          const intent = profile?.user_intent || onboarding?.user_intent;
          const concern = profile?.primary_concern || onboarding?.primary_concern;

          userContext = `
USER PROFILE (Use this as the source of truth):
- Age: ${profile?.age || 'Not specified'}
- Sex: ${profile?.sex || 'Not specified'}
USER INTENT: ${intent ?? "not set"} (wellness = preventive/maintenance guidance; condition = therapeutic intervention)
PRIMARY CONCERN: ${concern ?? "not specified"}
USER PRAKRITI (constitutional nature — lifelong): ${prakriti ?? "not yet assessed"}
USER VIKRITI (current imbalance): ${vikriti ?? "not yet assessed"}

DOSHA INTERPRETATION RULES:
- Single dosha (e.g. "pitta"): one dominant constitution / imbalance
- Dual dosha (e.g. "vata-pitta"): two comparably prominent doshas.
  First-named has slightly higher score. Both are therapeutically significant.
- "sama": all three doshas roughly equal (tri-doshic / Sama Prakriti).
  For Sama PRAKRITI: seasonal guidance applies — see seasonal rules below.
  For Sama VIKRITI: all three are currently elevated — see Vikriti conflict rules below.

SEASONAL RULES FOR SAMA PRAKRITI:
  Oct-Dec → Vata season: emphasise grounding, warmth, routine
  Jan-May → Kapha season: emphasise stimulation, lightness, movement
  Jun-Sep → Pitta season: emphasise cooling, surrender, shade
  Current month: ${new Date().toLocaleString("en-US", { month: "long" })}

VIKRITI CONFLICT RULES FOR DUAL/SAMA VIKRITI:
  Use PRIMARY CONCERN to determine the priority Dosha.
  If no primary concern: address the first-named Dosha in the dual string.
  When suggesting herbs or foods: favour those that pacify both Doshas where possible.
  When no herb/food pacifies both: choose the one that addresses the primary concern,
  and note the trade-off naturally in your response.
  Never prescribe a herb that strongly aggravates either Dosha in a dual Vikriti.

- Pattern Insights: ${onboarding?.pattern_insights ? onboarding.pattern_insights.join('; ') : 'None'}
- Allergies: ${(profile?.allergies || []).join(', ') || 'None known'}
- Medical History: ${profile?.health_data?.medical_history || 'None provided'}
- Diet: ${profile?.dietary_preference || profile?.health_data?.diet_type || 'Not specified'}

${profile?.life_stage ? `
--- WOMEN'S HEALTH CONTEXT ---
Life Stage: ${profile.life_stage}
Menstrual Pattern: ${Array.isArray(profile.menstrual_pattern) ? profile.menstrual_pattern.join(", ") : "Not specified"}
Hormonal Context: ${Array.isArray(profile.hormonal_context) ? profile.hormonal_context.join(", ") : "None reported"}
Flow Color: ${profile.flow_color || "Not provided"}
` : ""}

DO NOT ask for these details again. Treat them as already known.
If the user specifies they are asking for someone else, IGNORE this profile.
          `;
        }
      } catch (e) {
        logger.error("Failed to load profile and onboarding context", e);
      }
    }

    // 2. Build Intelligent Prompt
    const contextText = contextDocs.length > 0
      ? `ANCIENT TEXT CONTEXT:\n${contextDocs.map(d => `[Source: ${d.book_name}]\n${d.content}`).join('\n\n')}`
      : "The library archives are updating. Use your internal Ayurvedic training.";

    const prompt = `
USER QUERY: ${safeMessage}

${userContext}

${contextText}

INSTRUCTIONS FOR NANI:
1. If 'USER PROFILE' above is present and valid, USE IT to determine dosage and contraindications. DO NOT ask for Age/Sex/History.
2. If 'USER PROFILE' is missing AND the query requires health specifics (e.g. dosage), warmly ask for Age/Sex/History using: "Tell me your age, sex and any past medical history or other health concerns I should know about"
3. Use 'Prakriti' (permanent constitution) and 'Vikriti' (current state) to personalize the response. 
   - Ensure the remedy DOES NOT aggravate the Prakriti.
   - Ensure the remedy TARGETS the Vikriti (imbalance).
   - If user intent is 'wellness', focus on maintenance based on Prakriti.
   - If user intent is 'condition', focus on restoring Vikriti to Prakriti.
3. If the user query implies a different person (e.g. "for my daughter"), IGNORE the profile. CHECK if the query already contains their details (e.g. "She is 62" = Female, 62). If details are present/implied (e.g. "my mother" = Female), DO NOT ask for them again. Only ask for *missing* details.
4. Give the holistic remedy and ALWAYS include the [METADATA_START] JSON block at the very end.
5. The Markdown table for the REMEDY protocol MUST have 5 columns: BOTANICAL RX, DOSAGE, PREPARATION, FREQUENCY, CLINICAL EFFECTS.

BOTANICAL RX — DUAL/TRI DOSHA HERB SELECTION:
When USER VIKRITI is dual (e.g. "vata-pitta") or sama:
1. First, identify herbs that are tridoshic (pacify all three) or bidoshic (pacify both elevated Doshas). These are the safest and most effective choices for complex constitutions. Examples:
   - Tridoshic herbs: Triphala, Amalaki (especially cooling — good for Pitta), Haritaki (especially warming — good for Vata/Kapha), Shatavari (tridoshic with Pitta emphasis)
   - Vata-Pitta: Guduchi (Tinospora cordifolia) — cooling yet tonifying. Brahmi — calming and clarifying.
   - Pitta-Kapha: Neem — cooling and purifying. Turmeric — anti-inflammatory.
   - Vata-Kapha: Ashwagandha — warming and grounding.
2. If a condition-specific herb is needed that is not tridoshic/bidoshic: include it with a brief note in the Clinical Effects column. Format: "[herb effect]. Note: use with caution for [other elevated Dosha] — limit to [duration] or monitor [symptom]."
3. Never include a herb that strongly aggravates either elevated Dosha without flagging it. If no safe alternative exists, note this explicitly and recommend a shorter course.

6. Ensure the JSON 'detail' field starts exactly with the markdown table (no "Detailed table:" prefix).
        `;

    const res = await fetch('/api/gemini', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "stream",
        prompt: prompt,
        temperature: 0.3,
        config: { systemInstruction: SYSTEM_INSTRUCTION }
      })
    });

    if (!res.ok || !res.body) throw new Error("Stream connection failed");

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      yield decoder.decode(value);
    }

    if (currentUser && !isRefinement) {
      logAnalyticsEvent(safeMessage, hasRAG ? 'RAG' : 'AI', contextDocs.map(d => d.book_name || 'Tradition'));
    }
  } catch (error) {
    logger.error("[GeminiService] Chat Error:", error);
    yield "My dear, Nani had a dizzy spell. Please repeat your words once more.";
  }
};

export const generateYogaRoutine = async (
  ailmentId: string,
  userProfile: UserProfile | null,
  consultationResponse: string | null = null
): Promise<import("../types").YogaRoutine> => {
  if (!ailmentId.trim()) return { poses: [], classification: 'Acute', duration: '7 days' };

  // ── Step 1: Fetch RAG context docs (needed for both overrides and rationale) ──
  let contextDocs: RemedyDocument[] = [];
  try {
    const queryVector = await generateEmbedding(ailmentId);
    if (queryVector) {
      contextDocs = await searchVectorDatabase(ailmentId, queryVector);
    }
  } catch (e) { logger.error("Yoga RAG Error:", e); }

  const contextText = contextDocs.length > 0
    ? `ANCIENT TEXT CONTEXT:\n${contextDocs.map(d => `[Source: ${d.book_name}]\n${d.content}`).join('\n\n')}`
    : "Use standard therapeutic yoga knowledge.";

  // ── Step 2: Extract and merge consultation and RAG signals ────────────────
  let consultationSignals = consultationResponse
    ? extractYogaSignalsFromConsultation(consultationResponse)
    : undefined;

  // Extract signals from RAG docs and merge
  if (contextDocs.length > 0) {
    const ragText = contextDocs.map(d => d.content).join('\n');
    const ragSignals = extractYogaSignalsFromConsultation(ragText);

    if (!consultationSignals) {
      consultationSignals = ragSignals;
    } else {
      // Merge: RAG signals fill in overrides if they aren't already set by consultation
      if (!consultationSignals.recommendedPranayama) {
        consultationSignals.recommendedPranayama = ragSignals.recommendedPranayama;
      }
      if (!consultationSignals.foundationOverride) {
        consultationSignals.foundationOverride = ragSignals.foundationOverride;
      }
      if (!consultationSignals.activationOverride) {
        consultationSignals.activationOverride = ragSignals.activationOverride;
      }
      consultationSignals.mentionedPoses = [
        ...new Set([...consultationSignals.mentionedPoses, ...ragSignals.mentionedPoses])
      ];
    }
  }

  if (consultationSignals) {
    logger.log(
      `[YogaAid] Merged Signals — pranayama: ${consultationSignals.recommendedPranayama ?? 'none'}` +
      ` | foundation: ${consultationSignals.foundationOverride?.name ?? 'none'}` +
      ` | activation: ${consultationSignals.activationOverride?.name ?? 'none'}` +
      ` | mentioned: ${consultationSignals.mentionedPoses.join(', ') || 'none'}`
    );
  }

  // ── Step 3: Static Library Lookup + consultation/RAG overrides ─────────────
  const timeOfDay = getCurrentTimeOfDay();
  const dosha = userProfile?.vikriti_dosha ?? userProfile?.prakriti_dosha ?? 'sama';
  const curatedPoses = getYogaProtocol(ailmentId, dosha, timeOfDay, consultationSignals);

  logger.log(`[YogaAid] Final poses for "${ailmentId}" | dosha="${dosha}" | time=${timeOfDay}`);
  logger.log(`[YogaAid] Foundation=${curatedPoses.foundation.name} | Activation=${curatedPoses.activation.name} | Pranayama=${curatedPoses.pranayama.name} | Nature=${curatedPoses.nature.name}`);

  // ── Step 3: Profile context ────────────────────────────────────────────────
  const profileContext = userProfile ? `
USER PROFILE - ADAPT RATIONALE FOR:
- Age: ${userProfile.age}
- Sex: ${userProfile.sex || 'Not specified'}
- Intent: ${userProfile.user_intent === 'wellness' ? 'Wellness & Maintenance' : 'Condition Management'}
PRIMARY CONCERN: ${userProfile.primary_concern ?? "not specified"}
USER PRAKRITI (constitutional nature — lifelong): ${userProfile.prakriti_dosha ?? "not yet assessed"}
USER VIKRITI (current imbalance): ${userProfile.vikriti_dosha ?? userProfile.prakriti_dosha ?? "not yet assessed"}

DOSHA INTERPRETATION RULES:
- Single dosha (e.g. "pitta"): one dominant constitution / imbalance
- Dual dosha (e.g. "vata-pitta"): two comparably prominent doshas.
  First-named has slightly higher score. Both are therapeutically significant.
- "sama": all three doshas roughly equal (tri-doshic / Sama Prakriti).

SEASONAL RULES FOR SAMA PRAKRITI:
  Oct-Dec → Vata season: emphasise grounding, warmth, routine
  Jan-May → Kapha season: emphasise stimulation, lightness, movement
  Jun-Sep → Pitta season: emphasise cooling, surrender, shade
  Current month: ${new Date().toLocaleString("en-US", { month: "long" })}

VIKRITI CONFLICT RULES FOR DUAL/SAMA VIKRITI:
  Use PRIMARY CONCERN to determine the priority Dosha.
  If no primary concern: address the first-named Dosha in the dual string.
  When adapting poses: favour modifications that pacify both Doshas where possible.
  Never recommend intensity that aggravates either Dosha in a dual Vikriti.

- History/Injuries: ${userProfile.health_data?.medical_history || 'None'}
- Goal: ${userProfile.primary_goal}
INSTRUCTION: Adapt the rationale to be safe for any listed injuries. If the user has an injury, note modifications in doshaAdaptation.

YOGA AID — DUAL/TRI DOSHA SEQUENCING:
When USER VIKRITI is dual:
- Use PRIMARY CONCERN to identify the priority Dosha for phase sequencing.
- In per-pose doshaAdaptation: generate guidance for all three Doshas plus a "dual_priority" key combining the two relevant Dosha adaptations.

When USER PRAKRITI or VIKRITI is sama:
- Use the current season to determine the sequencing priority Dosha.
- In doshaAdaptation: show all three Dosha adaptations plus a "current season" note.
` : "";

  // ── Step 4: Build rationale-only prompt (poses are FIXED) ─────────────────
  // Signal context tells AI which poses came from the consultation
  const signalContext = (consultationSignals?.mentionedPoses.length ?? 0) > 0
    ? `\nCONSULTATION CONTEXT: The prior consultation specifically mentioned these movements: ` +
      `${consultationSignals!.mentionedPoses.join(', ')}. ` +
      `Where these have been incorporated into the protocol, reference them naturally in your rationale — e.g., "as discussed in your consultation".`
    : '';

  const rationalePrompt = `
${contextText}

${profileContext}

${YOGA_SPECIALIST_INSTRUCTION}

AILMENT: ${ailmentId}

IMPORTANT: The following poses have been PRE-SELECTED by the curated library.
DO NOT change or substitute any pose. Your ONLY task is to generate the
therapeutic rationale for each of these exact poses:

Phase 1 — Foundation : ${curatedPoses.foundation.name}
Phase 2 — Activation : ${curatedPoses.activation.name}
Phase 3 — Internal Balance (Pranayama): ${curatedPoses.pranayama.name}
Phase 4 — Nature Integration: ${curatedPoses.nature.name}

For each pose generate:
- benefit: therapeutic benefit specific to "${ailmentId}" (1-2 sentences)
- contraindications: one key contraindication to be aware of
- doshaNote: brief dosha adaptation note (≤15 words)
- doshaAdaptation: full vata/pitta/kapha adaptation object per spec below

Also determine classification (Acute/Chronic) and duration (7/21 days).
Generate phaseEmphasis if classification is Chronic.
Generate natureIntegrationDetail for Phase 4.
${signalContext}

${getMorningContextGuard(ailmentId, consultationResponse || '')}

OUTPUT FORMAT: JSON Object
  `;

  // ── Step 5: Call AI for rationale ─────────────────────────────────────────
  try {
    const res = await fetch('/api/gemini', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "generate",
        prompt: rationalePrompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: "OBJECT",
            properties: {
              classification: { type: "STRING", enum: ["Acute", "Chronic"] },
              duration: { type: "STRING" },
              poses: {
                type: "ARRAY",
                items: {
                  type: "OBJECT",
                  properties: {
                    phase: { type: "STRING" },
                    pose_name: { type: "STRING" },
                    type: { type: "STRING", enum: ["Asana", "Pranayama", "Exercise"] },
                    benefit: { type: "STRING" },
                    instructions: { type: "STRING" },
                    contraindications: { type: "STRING" },
                    doshaNote: { type: "STRING" },
                    doshaAdaptation: {
                      type: "OBJECT",
                      properties: {
                        vata: { type: "OBJECT", properties: { duration: { type: "STRING" }, breathPattern: { type: "STRING" }, intensity: { type: "STRING" }, props: { type: "STRING" }, mentalFocus: { type: "STRING" } }, required: ["duration", "breathPattern", "intensity", "props", "mentalFocus"] },
                        pitta: { type: "OBJECT", properties: { duration: { type: "STRING" }, breathPattern: { type: "STRING" }, intensity: { type: "STRING" }, props: { type: "STRING" }, mentalFocus: { type: "STRING" } }, required: ["duration", "breathPattern", "intensity", "props", "mentalFocus"] },
                        kapha: { type: "OBJECT", properties: { duration: { type: "STRING" }, breathPattern: { type: "STRING" }, intensity: { type: "STRING" }, props: { type: "STRING" }, mentalFocus: { type: "STRING" } }, required: ["duration", "breathPattern", "intensity", "props", "mentalFocus"] }
                      },
                      required: ["vata", "pitta", "kapha"]
                    }
                  },
                  required: ["phase", "pose_name", "type", "benefit", "instructions", "contraindications", "doshaAdaptation"]
                }
              },
              phaseEmphasis: {
                type: "OBJECT",
                properties: {
                  dominantDosha: { type: "STRING" },
                  foundationWeight: { type: "STRING", enum: ["extended", "standard", "reduced"] },
                  activationWeight: { type: "STRING", enum: ["extended", "standard", "reduced"] },
                  internalBalanceWeight: { type: "STRING", enum: ["extended", "standard", "reduced"] },
                  pacingDescriptor: { type: "STRING" },
                  sequencingRationale: { type: "STRING" }
                },
                required: ["dominantDosha", "foundationWeight", "activationWeight", "internalBalanceWeight", "pacingDescriptor", "sequencingRationale"]
              },
              natureIntegrationDetail: {
                type: "OBJECT",
                properties: {
                  activityName: { type: "STRING" },
                  benefit: { type: "STRING" },
                  textualSource: { type: "STRING" },
                  doshaGuidance: { type: "OBJECT", properties: { vata: { type: "OBJECT", properties: { environment: { type: "STRING" }, timing: { type: "STRING" }, avoidance: { type: "STRING" } }, required: ["environment", "timing", "avoidance"] }, pitta: { type: "OBJECT", properties: { environment: { type: "STRING" }, timing: { type: "STRING" }, avoidance: { type: "STRING" } }, required: ["environment", "timing", "avoidance"] }, kapha: { type: "OBJECT", properties: { environment: { type: "STRING" }, timing: { type: "STRING" }, avoidance: { type: "STRING" } }, required: ["environment", "timing", "avoidance"] } }, required: ["vata", "pitta", "kapha"] },
                  steps: { type: "ARRAY", items: { type: "OBJECT", properties: { stepNumber: { type: "NUMBER" }, title: { type: "STRING" }, description: { type: "STRING" }, duration: { type: "STRING" } }, required: ["stepNumber", "title", "description", "duration"] } },
                  sensoryPractice: { type: "OBJECT", properties: { sight: { type: "STRING" }, sound: { type: "STRING" }, smell: { type: "STRING" }, touch: { type: "STRING" } }, required: ["sight", "sound", "smell", "touch"] },
                  optimalTiming: { type: "OBJECT", properties: { vata: { type: "STRING" }, pitta: { type: "STRING" }, kapha: { type: "STRING" } }, required: ["vata", "pitta", "kapha"] },
                  contraindications: { type: "STRING" },
                  imageSearchQuery: { type: "STRING" }
                }
              }
            },
            required: ["classification", "duration", "poses"]
          }
        }
      })
    });

    const data = await res.json();
    const parsed = JSON.parse(data.text || '{"poses":[], "classification": "Acute", "duration": "7 days"}');

    // ── Step 6: Override pose_name and instructions from library ──────────────
    // Ensures the static library names and practice descriptions are used
    // even if the AI tried to change them.
    const poseOrder: Array<keyof YogaProtocolTemplate> = ['foundation', 'activation', 'pranayama', 'nature'];
    const activeDosha = (dosha.includes('pitta') ? 'pitta' : dosha.includes('kapha') ? 'kapha' : 'vata') as 'vata' | 'pitta' | 'kapha';
    
    if (Array.isArray(parsed.poses)) {
      // Step A: Map existing poses to library content
      parsed.poses = parsed.poses.map((p: any, idx: number) => {
        const key = poseOrder[idx];
        const curated = key ? curatedPoses[key] : null;
        if (!curated) return p;
        return {
          ...p,
          pose_name: curated.name,                           // locked from library
          instructions: curated.practiceDescription,          // locked from library
          vector_description: curated.practiceDescription,    // used for AI generation prompts
          _imagePath: curated.imagePath,                      // for image resolution
        };
      });

      // Step B: Synthesize 4th Nature pose if missing from array but exists in AI detail
      // The AI schema puts Nature in a top-level property, but the UI expects it in the poses array.
      if (parsed.poses.length === 3 && parsed.natureIntegrationDetail && curatedPoses.nature) {
        const natureDetail = parsed.natureIntegrationDetail;
        const curatedNature = curatedPoses.nature;
        
        const naturePose: YogaPose = {
          phase: "Nature Integration",
          pose_name: curatedNature.name,
          type: "Exercise",
          benefit: natureDetail.benefit || curatedNature.practiceDescription,
          instructions: curatedNature.practiceDescription,
          vector_description: natureDetail.sensoryPractice ? `Environmental: ${natureDetail.doshaGuidance[activeDosha].environment}. Sensory: ${Object.values(natureDetail.sensoryPractice).join(', ')}` : curatedNature.practiceDescription,
          contraindications: natureDetail.contraindications || "Standard outdoor safety applies.",
          doshaNote: "Restorative nature-based practice",
          _imagePath: curatedNature.imagePath,
          doshaAdaptation: {
            vata: { 
              duration: natureDetail.optimalTiming?.vata || "15-20 mins", 
              breathPattern: "Natural & steady", 
              intensity: "Gentle", 
              props: "Comfortable shoes if needed", 
              mentalFocus: natureDetail.sensoryPractice?.touch || "Grounding" 
            },
            pitta: { 
              duration: natureDetail.optimalTiming?.pitta || "10-15 mins", 
              breathPattern: "Cooling", 
              intensity: "Relaxed", 
              props: "Shady setting", 
              mentalFocus: natureDetail.sensoryPractice?.sight || "Calm observation" 
            },
            kapha: { 
              duration: natureDetail.optimalTiming?.kapha || "20-30 mins", 
              breathPattern: "Deep & rhythmic", 
              intensity: "Active", 
              props: "Open nature setting", 
              mentalFocus: natureDetail.sensoryPractice?.sound || "Vibrational awareness" 
            }
          }
        };
        parsed.poses.push(naturePose);
      }
    }

    return parsed;
  } catch (e) {
    logger.error("[YogaAid] generateYogaRoutine failed:", e);
    return { poses: [], classification: 'Acute', duration: '7 days' };
  }
};


const getAilmentContextGuards = (ailment: string): string => {
  const target = ailment.toLowerCase();

  if (['insomnia', 'sleep', 'sleep aid', 'sleeplessness', 'sleep disorder'].some(a => target.includes(a))) {
    return `
SLEEP AID CONTEXT GUARD:
Warm milk, nutmeg milk, ashwagandha milk, and golden milk are appropriate Ayurvedic sleep remedies but are dairy-based. They must only appear as standalone bedtime drinks or paired with light vegetarian snacks. Never pair any dairy-based drink with meat, poultry, or fish anywhere in the same day's meal plan. Any day that includes a milk-based remedy must have fully vegetarian meals for the entire day.
`;
  }

  if (['joint pain', 'arthritis', 'inflammation', 'joint'].some(a => target.includes(a))) {
    return `
JOINT HEALTH CONTEXT GUARD:
Avoid recommending nightshades (tomato, eggplant, bell pepper) combined with heavy dairy in the same meal. Avoid sour or fermented foods combined with dairy.
`;
  }

  return "";
};

const getMorningContextGuard = (ailment: string, consultationText: string): string => {
  const isMorningProtocol = /morning|wake up|start of day|upon waking|sunrise|am routine/i
    .test(ailment + ' ' + consultationText);
  
  if (!isMorningProtocol) return '';

  return `
MORNING PROTOCOL CONTEXT:
This is a morning wellness protocol. Pose selection must follow 
these morning-specific rules:

Foundation phase: Use a gentle grounding pose that is 
accessible immediately upon waking. Tadasana, Sukhasana, 
or gentle Cat-Cow are ideal. Avoid complex or unfamiliar 
clinical techniques.

Internal Balance phase: Use Anuloma Viloma (Alternate Nostril 
Breathing) as the pranayama. This is the classical Ayurvedic 
morning pranayama that balances both hemispheres and prepares 
the mind for the day. Do NOT use Sheetali for morning protocols 
— it is a midday cooling technique.

Nature Integration phase: Morning walk in natural light, 
sun gazing, or barefoot grounding on grass are ideal.

All poses must be recognisable to a general wellness audience.
  `;
};

const buildAilmentDietaryBlock = (ailmentId: string): string => {
  const rule = getAilmentDietaryRules(ailmentId);
  if (!rule) return "";

  let proteinPhrase = "";
  if (rule.proteinGuidance === 'plant_only') {
    proteinPhrase = "Use ONLY plant-based proteins. No animal protein of any kind including meat, poultry, fish, seafood, or eggs is permitted in any meal.";
  } else if (rule.proteinGuidance === 'low_animal') {
    proteinPhrase = "Minimise animal protein. Prefer plant-based sources. If animal protein is used, limit strictly to small amounts of lean poultry only.";
  } else {
    proteinPhrase = "No specific protein restriction beyond user profile preferences.";
  }

  let sodiumPhrase = "";
  if (rule.sodiumRestriction) {
    sodiumPhrase = "\nSODIUM RESTRICTION: All meals must be explicitly low sodium. Do not add salt to any recipe. Never use salty, pickled, or cured ingredients.";
  }

  return `
AILMENT-SPECIFIC DIETARY RULES FOR ${ailmentId.toUpperCase()}:
${rule.notes}

STRICTLY FORBIDDEN (Do NOT use these ingredients):
${rule.strictlyAvoid.map(item => `- ${item}`).join('\n')}

PREFERRED INGREDIENTS (Include these where possible):
${rule.prefer.map(item => `- ${item}`).join('\n')}

PROTEIN GUIDANCE: ${proteinPhrase}${sodiumPhrase}
`;
};

function extractDietarySignalsFromConsultation(consultationText: string): DietarySignals {
  const signals: DietarySignals = {
    mentionedFoods: [],
    mentionedIngredients: [],
    avoidanceSignals: [],
    constitutionType: null,
    tonics: [],
    preparationGuidance: [],
    primaryFoodCategory: null,
    specificDishMentions: []
  };

  if (!consultationText) return signals;

  const foodCategoryPatterns: [RegExp, string][] = [
    [/millet|siri dhaanya|ambali/i, 'millets'],
    [/raw food|raw diet/i, 'raw foods'],
    [/ferment/i, 'fermented foods'],
    [/soup|broth|liquid diet/i, 'soups and broths'],
    [/fast|fasting/i, 'light fasting foods'],
    [/khichdi|kitchari/i, 'khichdi and dal dishes'],
  ];

  for (const [pattern, category] of foodCategoryPatterns) {
    if (pattern.test(consultationText)) {
      signals.primaryFoodCategory = category;
      break;
    }
  }

  const dishPatterns = [
    'ambali', 'khichdi', 'kitchari', 'upma', 'dosa', 'idli',
    'rasam', 'sambar', 'dal', 'porridge', 'stew', 'soup'
  ];
  signals.specificDishMentions = dishPatterns.filter(dish =>
    consultationText.toLowerCase().includes(dish)
  );

  const prepPatterns = [
    /soak[^\.\,]*/gi,
    /always prepare[^\.\,]*/gi,
    /never eat[^\.\,]*/gi,
    /cook[^\.\,]*before[^\.\,]*/gi,
    /rinse[^\.\,]*/gi,
  ];

  const prepGuidance: string[] = [];
  prepPatterns.forEach(pattern => {
    const matches = consultationText.match(pattern);
    if (matches) prepGuidance.push(...matches.map(m => m.trim()));
  });

  const nonFoodKeywords = [
    'nostril', 'nasal', 'inhale', 'exhale', 'breathe', 'breathing',
    'sniff', 'blow', 'rinse your nose', 'rinse your mouth',
    'massage', 'apply to skin', 'topical', 'rub on',
    'exercise', 'walk', 'meditate', 'yoga', 'pose',
    'sleep', 'rest', 'lie down'
  ];

  signals.preparationGuidance = prepGuidance.filter((guidance: string) => {
    const guidanceLower = guidance.toLowerCase();
    const isNonFood = nonFoodKeywords.some(kw => guidanceLower.includes(kw));
    if (isNonFood) {
      console.log(`[NutriHeal RAG] Filtered non-food guidance: "${guidance}"`);
    }
    return !isNonFood;
  });
  
  return signals;
}


export const generateDietPlan = async (
  ailmentId: string, 
  userProfile: UserProfile | null,
  consultationResponse: string | null = null
): Promise<import("../types").NutriHealPlan | { meals: [] }> => {
  if (!ailmentId.trim()) return { meals: [] };

  // 1. Categorize Ailment (Simple keyword check)
  const target = ailmentId.toLowerCase();
  let category: RecipeCategory = RecipeCategory.DIGESTIVE; // default
  if (['joint', 'bone', 'muscle', 'arthritis', 'workout', 'post-workout', 'exercise', 'athletic', 'fitness', 'strength'].some(k => target.includes(k))) category = RecipeCategory.MUSCULOSKELETAL;
  else if (['cold', 'cough', 'lung', 'resp', 'sinus', 'flu'].some(k => target.includes(k))) category = RecipeCategory.RESPIRATORY;
  else if (['heart', 'blood pressure', 'cholesterol', 'cardio'].some(k => target.includes(k))) category = RecipeCategory.CARDIOVASCULAR;
  else if (['kidney', 'renal', 'uti', 'urine'].some(k => target.includes(k))) category = RecipeCategory.RENAL;
  else if (['skin', 'acne', 'eczema'].some(k => target.includes(k))) category = RecipeCategory.DERMATOLOGICAL;
  else if (['nerve', 'brain', 'sleep', 'anxiety', 'stress', 'neuro'].some(k => target.includes(k))) category = RecipeCategory.NEUROLOGICAL;
  else if (['diabetes', 'hormone', 'pcos', 'thyroid'].some(k => target.includes(k))) category = RecipeCategory.ENDOCRINE;
  else if (['immune', 'fever', 'recovery', 'hiv'].some(k => target.includes(k))) category = RecipeCategory.IMMUNE;

  // 2. Fetch Static Data
  let recipes = getRecipesByAilment(ailmentId);
  let primaryRecipes = recipes.filter(r => !r.herbalTea);
  let herbalTonics = recipes.filter(r => r.herbalTea);

  // Fallback: If broad terms (like "Pitta Balance") return no specific recipes, 
  // provide the best matches for the overall category.
  if (primaryRecipes.length === 0) {
    primaryRecipes = NUTRIHEAL_RECIPES.filter(r => r.category === category && !r.herbalTea);
  }
  if (herbalTonics.length === 0) {
    herbalTonics = NUTRIHEAL_RECIPES.filter(r => r.category === category && r.herbalTea);
  }

  primaryRecipes = primaryRecipes.slice(0, 6);
  herbalTonics = herbalTonics.slice(0, 3);

  const guidelines = STATIC_BASE_GUIDELINES[category] || STATIC_BASE_GUIDELINES.DIGESTIVE;
  
  // 3. Determine Duration label
  const isOngoing = ['muscle', 'workout', 'post-workout', 'exercise', 'athletic', 'fitness', 'strength', 'digestive health', 'weight'].some(k => target.includes(k));
  const isChronic = !isOngoing && ['diabetes', 'hypertension', 'arthritis', 'anxiety', 'insomnia', 'chronic'].some(k => target.includes(k));
  const isAcute  = !isOngoing && !isChronic;
  const duration = isOngoing ? 'Nutrition Guidance' : isChronic ? '21-Day' : '7-Day';

  // 4. Generate AI Personalization Note
  const dosha = userProfile?.vikriti_dosha ?? userProfile?.prakriti_dosha ?? 'sama';
  const season = new Date().toLocaleString("en-US", { month: "long" });
  
  const prompt = `
${NUTRI_HEAL_SPECIALIST_PROMPT}

CONTEXT:
Ailment: ${ailmentId}
Category: ${category}
User Dosha: ${dosha}
Current Month/Season: ${season}
Duration: ${duration}
Recipes in Plan: ${primaryRecipes.map(r => r.name).join(', ')}
Herbal Tonics: ${herbalTonics.map(r => r.name).join(', ')}

Strictly follow the 150-word limit.
  `;

  let personalizationNote = "Focus on warm, easily digestible meals as you begin your healing journey. These curated recipes are selected to balance your internal environment and support your recovery.";

  try {
    const res = await fetch('/api/gemini', {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "generate",
        prompt: prompt,
        config: { temperature: 0.7 }
      })
    });
    const data = await res.json();
    if (data.text) personalizationNote = data.text.trim();
  } catch (e) {
    logger.error("[NutriHeal] AI personalization failed, using fallback.", e);
  }

  return {
    ailment: ailmentId,
    category: category,
    personalizationNote,
    guidelines,
    primaryRecipes,
    herbalTonics,
    duration
  };
};
